import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RadarChart from '../components/knowledge-graph/RadarChart';
import StrengthsWeaknesses from '../components/knowledge-graph/StrengthsWeaknesses';
import LanguageFoundations from '../components/knowledge-graph/LanguageFoundations';
import CategoryDetailSection from '../components/knowledge-graph/CategoryDetailSection';
import FsrsExplainer from '../components/knowledge-graph/FsrsExplainer';
import CaffaAbilityCard from '../components/knowledge-graph/CaffaAbilityCard';
import {
    fetchProficiencySummary,
    fetchGraphTree,
    fetchProficiency,
    initializeProficiency,
    type ProficiencySummary,
    type GraphTreeNode,
    type UserNodeMastery,
} from '../services/knowledgeGraphService';
import {
    getMyCaffaAbilityVector,
    type AbilityVectorResponse,
} from '../services/caffaService';
import './KnowledgeGraphPage.css';

const CATEGORY_ORDER = ['grammar', 'vocabulary', 'usage'];

const KnowledgeGraphPage: React.FC = () => {
    const { t, i18n } = useTranslation('knowledgeGraph');
    const { user } = useAuth();
    const [summary, setSummary] = useState<ProficiencySummary | null>(null);
    const [tree, setTree] = useState<GraphTreeNode[]>([]);
    const [masteryMap, setMasteryMap] = useState<Map<string, UserNodeMastery>>(new Map());
    const [caffaVector, setCaffaVector] = useState<AbilityVectorResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [initializing, setInitializing] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [summaryData, treeData, proficiencyData, caffaData] = await Promise.all([
                fetchProficiencySummary(i18n.language),
                fetchGraphTree(),
                fetchProficiency(),
                getMyCaffaAbilityVector().catch(() => null),
            ]);
            setSummary(summaryData);
            setTree(treeData);
            setCaffaVector(caffaData);

            // Build nodeId → mastery lookup map
            const map = new Map<string, UserNodeMastery>();
            for (const m of proficiencyData) {
                map.set(m.nodeId, m);
            }
            setMasteryMap(map);
        } catch (err: any) {
            setError(err?.response?.data?.message || t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [i18n.language]);

    useEffect(() => {
        if (loading) return;
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;
        const target = document.getElementById(hash);
        if (!target) return;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    }, [loading]);

    const handleInitialize = async () => {
        try {
            setInitializing(true);
            await initializeProficiency();
            await loadData();
        } catch (err: any) {
            setError(err?.response?.data?.message || t('initError'));
        } finally {
            setInitializing(false);
        }
    };

    const radarLabels = [
        t('radar.vocabulary'),
        t('radar.grammar'),
        t('radar.sentence'),
        t('radar.listening'),
        t('radar.speaking'),
        t('radar.writing'),
    ];

    // Compute overall CEFR from 6-dimension weighted scores (not the 3-category average)
    const computeOverallCefr = (s: ProficiencySummary): string => {
        const dd = s.dimensionDetails;
        if (!dd) return s.overallCefrEstimate;
        const weights = { vocabulary: 0.18, grammar: 0.18, sentence: 0.12, listening: 0.18, speaking: 0.16, writing: 0.18 };
        const dims = ['vocabulary', 'grammar', 'sentence', 'listening', 'speaking', 'writing'] as const;
        let totalWeight = 0;
        let weightedSum = 0;
        for (const d of dims) {
            const score = dd[d]?.score;
            if (score != null) {
                weightedSum += score * weights[d];
                totalWeight += weights[d];
            }
        }
        if (totalWeight === 0) return s.overallCefrEstimate;
        const overall = weightedSum / totalWeight;
        if (overall >= 0.9) return 'C2';
        if (overall >= 0.75) return 'C1';
        if (overall >= 0.6) return 'B2';
        if (overall >= 0.4) return 'B1';
        if (overall >= 0.2) return 'A2';
        return 'A1';
    };

    if (loading) {
        return (
            <div className="kg-page">
                <div className="kg-page__loading">{t('common:loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="kg-page">
                <div className="kg-page__error">
                    <p>{error}</p>
                    <button onClick={loadData} className="kg-page__retry-btn">{t('common:retry')}</button>
                </div>
            </div>
        );
    }

    const isEmpty = summary
        && summary.totalNodesLearned === 0
        && summary.totalNodesEstimated === 0
        && summary.categoryScores.grammar === 0;

    // Sort category root nodes by defined order
    const categoryRoots = CATEGORY_ORDER
        .map(cat => tree.find(node => node.category === cat))
        .filter((node): node is GraphTreeNode => !!node);

    return (
        <div className="kg-page">
            <div className="kg-page__header">
                <h1 className="kg-page__title">{t('title')}</h1>
                <p className="kg-page__subtitle">{t('subtitle')}</p>
            </div>

            {isEmpty ? (
                <div className="kg-page__empty">
                    <div className="kg-page__empty-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #667eea)" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M8 12l2 2 4-4"/>
                        </svg>
                    </div>
                    <h2>{t('emptyTitle')}</h2>
                    <p>{t('emptyDescription')}</p>
                    <button
                        className="kg-page__init-btn"
                        onClick={handleInitialize}
                        disabled={initializing}
                    >
                        {initializing ? t('common:loading') : t('initFromPlacement')}
                    </button>
                </div>
            ) : summary && (
                <div className="kg-page__content">
                    <div className="kg-page__overview-card">
                        <div className="kg-page__radar-wrap">
                            <RadarChart
                                data={summary.radarData}
                                labels={radarLabels}
                                dimensionDetails={summary.dimensionDetails}
                                confidence={summary.confidence}
                                dataPoints={summary.dataPoints}
                                overallCefr={computeOverallCefr(summary)}
                            />
                        </div>
                        {!summary.isPlacementOnly && summary.totalNodesLearned > 0 && (
                            <p className="kg-page__disclaimer">{t('placementDisclaimer')}</p>
                        )}
                    </div>

                    {summary.isPlacementOnly ? (
                        <div className="kg-page__placement-hint">
                            <p>{t('placementOnlyHint')}</p>
                        </div>
                    ) : (
                        <StrengthsWeaknesses
                            strengths={summary.strengths}
                            weaknesses={summary.weaknesses}
                        />
                    )}

                    <CaffaAbilityCard vector={caffaVector} />

                    <LanguageFoundations />

                    <FsrsExplainer />

                    {/* Reference Guides */}
                    <div className="kg-page__guides">
                        <h2 className="kg-page__section-title">{t('referenceGuides.title')}</h2>
                        <p className="kg-page__section-desc">{t('referenceGuides.desc')}</p>
                        <div className="kg-page__guides-grid">
                            <Link to="/knowledge/phonics" className="kg-guide-card kg-guide-card--phonics">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                    <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                                </svg>
                                <span className="kg-guide-card__title">{t('referenceGuides.phonics')}</span>
                                <span className="kg-guide-card__desc">{t('referenceGuides.phonicsDesc')}</span>
                            </Link>
                            <Link to="/knowledge/morphology" className="kg-guide-card kg-guide-card--morphology">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/>
                                </svg>
                                <span className="kg-guide-card__title">{t('referenceGuides.morphology')}</span>
                                <span className="kg-guide-card__desc">{t('referenceGuides.morphologyDesc')}</span>
                            </Link>
                            <Link to="/knowledge/prosody" className="kg-guide-card kg-guide-card--prosody">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18V5l12-2v13"/>
                                    <circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                                </svg>
                                <span className="kg-guide-card__title">{t('referenceGuides.prosody')}</span>
                                <span className="kg-guide-card__desc">{t('referenceGuides.prosodyDesc')}</span>
                            </Link>
                            <Link to="/knowledge/sight-words" className="kg-guide-card kg-guide-card--sightWords">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                </svg>
                                <span className="kg-guide-card__title">{t('referenceGuides.sightWords')}</span>
                                <span className="kg-guide-card__desc">{t('referenceGuides.sightWordsDesc')}</span>
                            </Link>
                        </div>
                    </div>

                    {/* Category detail breakdown */}
                    {categoryRoots.length > 0 && (
                        <div className="kg-page__details">
                            <h2 className="kg-page__section-title">{t('detailBreakdown')}</h2>
                            <div className="kg-page__details-grid">
                                {categoryRoots.map(catNode => (
                                    <CategoryDetailSection
                                        key={catNode.id}
                                        categoryNode={catNode}
                                        masteryMap={masteryMap}
                                        isPlacementOnly={summary.isPlacementOnly}
                                        lang={i18n.language}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KnowledgeGraphPage;
